import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { SiteService } from "@/services/Site/SiteServive";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThrowError } from "@/api/callApi";
import { showToastNotification } from "@/utility/helper";
import type { IFilter, IPageInfo } from "@/interface/common.interface";
import TablePagination from "@/components/common/TablePagination";
import type { TableColumn } from "@/components/common/CommonTable";
import { Button } from "@/components/ui/button";
import { RoutesLink } from "@/utility/RouteLinks";
import {
  Pencil,
  Plus,
  Trash,
  TriangleAlert,
  MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CommonTable from "@/components/common/CommonTable";
import CommonModal from "@/components/common/CommonModal";
import type { site } from "@/interface/site.interface";
import CommonFilter from "@/components/common/CommonFilter";
import { useAppFilters } from "@/hooks/useAppFilters";

const Site: React.FC = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState<site[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");

  const {
    filterValues,
    queryString: filterQueryString,
    branchSelectNode,
    filterSelectorsNode,
  } = useAppFilters({
    enabled: ["branch"],
    onChange: () => {
      setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    },
  });

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const [filter, setFilter] = useState<IFilter>({
    search: "",
  });

  const handleSearchChange = (value: string) => {
    setFilter((prev) => {
      if (prev.search === value) return prev;
      return { ...prev, search: value };
    });
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const handleGetSites = async (
    currentPage = paginationData.currentPage,
    currentLimit = paginationData.pageSize,
  ) => {
    setIsLoading(true);
    try {
      const searchParam = filter.search
        ? `&search=${encodeURIComponent(filter.search)}`
        : "";
      const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${searchParam}${filterQueryString}`;
      const response = await SiteService?.getSite(PATH);
      if (response?.status) {
        setSites(response?.data?.site || []);
        setPaginationData((prev) => ({
          ...prev,
          total: response?.data?.pageInfo?.total || 0,
          totalPages: response?.data?.pageInfo?.totalPages || 0,
        }));
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSite = async () => {
    try {
      const response = await SiteService?.deleteSite(deleteId);
      if (response?.status) {
        showToastNotification("success", response?.message);
        toggleDeleteDialog("");
        handleGetSites();
      } else {
        showToastNotification("danger", response?.message);
      }
    } catch (error) {
      ThrowError(error);
    }
  };

  useEffect(() => {
    handleGetSites(paginationData.currentPage, paginationData.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationData.currentPage, paginationData.pageSize, filter.search, filterValues.branchId]);

  const toggleDeleteDialog = (id: string | null) => {
    setDeleteId(id ?? "");
    setDeleteDialogOpen(!deleteDialogOpen);
  };

  const columns: TableColumn<site>[] = [
    {
      key: 'sNo',
      header: 'S.No',
      render: (_item: site, index: number) => (
        <span className="text-muted-foreground font-medium text-sm">{String(
          (paginationData.currentPage - 1) * paginationData.pageSize + index + 1
        ).padStart(2, "0")}</span>
      )
    },
    {
      key: "aliasId",
      header: "Alias ID",
      render: (item: site) => (
        <span className="font-semibold capitalize">{item?.aliasId || "N/A"}</span>
      )
    },
    {
      key: "name",
      header: "Site Name",
      render: (item: site) => (
        <span className="font-bold capitalize">{item?.name || "N/A"}</span>
      ),
    },
    {
      key: "branch",
      header: "Branch",
      render: (item: site) => (
        <span className=" font-semibold capitalize">{item?.branch?.name || "N/A"}</span>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (item: site) => <span className="font-semibold capitalize">{item?.type}</span>,
    },
    {
      key: "searchedLocation",
      header: "Location",
      render: (item: site) => (
        <span className="font-semibold capitalize">{item?.searchedLocation || "N/A"}</span>
      ),
    },
    {
      key: "range",
      header: "Range (m)",
      render: (item: site) => (
        <span className="font-medium">{item?.range || "N/A"}</span>
      ),
    },
    {
      key: "isPunchAllowed",
      header: "Punch Allowed",
      render: (item: site) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.isPunchAllowed ? "text-green-600 border-green-600 bg-green-50" : "text-destructive border-destructive bg-destructive/10"}`}
        >
          {item?.isPunchAllowed ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item: site) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.status ? "text-green-600 border-green-600 bg-green-50" : "text-destructive border-destructive bg-destructive/10"}`}
        >
          {item?.status ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item: site) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-[170px] rounded-xl shadow-lg border-slate-100"
          >
            <DropdownMenuItem
              onClick={() => navigate(`${RoutesLink?.editSite}/${item?.id}`)}
              className="cursor-pointer"
            >
              <Pencil className="h-4 w-4" />
              Edit Site
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => toggleDeleteDialog(item?.id ?? null)}
              className="cursor-pointer text-red-500"
            >
              <Trash className="h-4 w-4" />
              Delete Site
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <>
      <Layout>
        <div className="space-y-6">
          <MainHeading
            title="Sites"
            isBackButtonVisible={true}
            buttonText="Add Site"
            buttonIcon={<Plus />}
            onClick={() => {
              navigate(RoutesLink?.addSite);
            }}
          />
          <CommonFilter
            searchValue={filter?.search || ""}
            onSearchChange={handleSearchChange}
            additionalFilters={filterSelectorsNode || branchSelectNode}
          />
          <CommonTable data={sites} columns={columns} loading={isLoading} />
          <TablePagination
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            handleChangePageSize={handleChangePageSize}
            isLoading={isLoading}
          />
        </div>
      </Layout>
      <CommonModal
        title=""
        show={deleteDialogOpen}
        onClose={() => toggleDeleteDialog("")}
        size="md"
      >
        <div className="text-center p-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 text-red-600 mb-4">
            <TriangleAlert className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold mb-2">Delete Site</h2>
          <div className="text-sm text-gray-500 max-w-sm mx-auto">
            Are you sure you want to delete this Site? This action cannot be undone.
          </div>
          <div className="mt-8 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => toggleDeleteDialog("")}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleDeleteSite}
            >
              Delete
            </Button>
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default Site;
